import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2 } from "lucide-react";
import { mget, mpost, mdel, errMsg, eur, fmtDate, todayISO } from "@/manager/lib";
import { PageHeader, Card, Field, KindPill, Empty, goldBtn } from "@/manager/ui";

const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

export default function Finance() {
  const navigate = useNavigate();
  const [ym, setYm] = useState(() => todayISO().slice(0, 7).split("-").map(Number));
  const [d, setD] = useState(null);
  const [fx, setFx] = useState({ concept: "", amount: "", date: todayISO() });
  const load = useCallback(() => mget("/finance/summary", { year: ym[0], month: ym[1] }).then(setD), [ym]);
  useEffect(() => { load(); }, [load]);
  const step = (n) => { const dt = new Date(ym[0], ym[1] - 1 + n, 1); setYm([dt.getFullYear(), dt.getMonth() + 1]); };
  const addFixed = async () => {
    try { await mpost("/expenses", { ...fx, amount: Number(fx.amount), fixed: true, category: "fijo" }); setFx({ ...fx, concept: "", amount: "" }); load(); toast.success("Gasto fijo añadido"); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!d) return <p className="text-sm">Cargando…</p>;
  const t = d.totals;
  const tiles = [["Ingreso previsto", t.income_expected], ["Cobrado", t.collected], ["Pendiente de cobro", t.pending], ["Costes reales", t.costs_real],
    ["Margen eventos", t.margin_real], ["Gastos fijos", d.fixed_total], ["Resultado tras fijos", d.net_after_fixed]];
  return (
    <div data-testid="manager-finance">
      <PageHeader eyebrow="Finanzas" title="Resumen mensual">
        <div className="flex items-center gap-2">
          <button onClick={() => step(-1)} className="w-9 h-9 rounded-full border bg-white flex items-center justify-center" data-testid="fin-prev"><ChevronLeft size={16} /></button>
          <span className="font-display text-xl w-44 text-center" data-testid="fin-month">{MONTHS[ym[1] - 1]} {ym[0]}</span>
          <button onClick={() => step(1)} className="w-9 h-9 rounded-full border bg-white flex items-center justify-center" data-testid="fin-next"><ChevronRight size={16} /></button>
        </div>
      </PageHeader>
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3 mb-6">
        {tiles.map(([l, v]) => <Card key={l} className="p-4"><p className={`font-display text-xl ${v < 0 ? "text-rose-600" : ""}`}>{eur(v)}</p><p className="text-[10px] uppercase tracking-[0.14em] text-[#6b6a66]">{l}</p></Card>)}
      </div>
      <Card className="overflow-x-auto mb-6">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-[0.14em] text-[#6b6a66] bg-[#F7F5F0]"><tr>{["Fecha", "Evento", "", "Previsto", "Cobrado", "Pendiente", "Costes", "Margen real"].map((h, i) => <th key={i} className="text-left px-4 py-3">{h}</th>)}</tr></thead>
          <tbody>
            {d.events.map((e) => (
              <tr key={e.id} onClick={() => navigate(`/manager/eventos/${e.id}?tab=finanzas`)} className="border-t border-[#f0ece4] cursor-pointer hover:bg-[#F7F5F0]" data-testid={`fin-row-${e.id}`}>
                <td className="px-4 py-2">{fmtDate(e.date)}</td><td className="px-4 py-2">{e.title}</td><td className="px-4 py-2"><KindPill kind={e.kind} /></td>
                <td className="px-4 py-2">{eur(e.income_expected)}</td><td className="px-4 py-2">{eur(e.collected)}</td>
                <td className={`px-4 py-2 ${e.pending ? "text-amber-700" : ""}`}>{eur(e.pending)}</td><td className="px-4 py-2">{eur(e.costs_real)}</td>
                <td className={`px-4 py-2 font-medium ${e.margin_real < 0 ? "text-rose-600" : "text-emerald-700"}`}>{eur(e.margin_real)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!d.events.length && <Empty>Sin eventos este mes.</Empty>}
      </Card>
      <Card className="p-5">
        <h3 className="font-display text-lg mb-3">Gastos fijos del mes</h3>
        <div className="flex flex-wrap gap-2 items-end mb-3">
          <Field label="Concepto" className="w-48"><Input value={fx.concept} onChange={(e) => setFx({ ...fx, concept: e.target.value })} placeholder="Alquiler, luz…" data-testid="fixed-concept" /></Field>
          <Field label="Importe (€)" className="w-32"><Input type="number" step="0.01" value={fx.amount} onChange={(e) => setFx({ ...fx, amount: e.target.value })} data-testid="fixed-amount" /></Field>
          <Field label="Fecha" className="w-40"><Input type="date" value={fx.date} onChange={(e) => setFx({ ...fx, date: e.target.value })} data-testid="fixed-date" /></Field>
          <Button className={goldBtn} onClick={addFixed} data-testid="fixed-add">Añadir</Button>
        </div>
        {d.fixed_expenses.map((x) => <p key={x.id} className="text-sm flex justify-between py-1 border-t border-[#f0ece4]"><span>{fmtDate(x.date)} · {x.concept}</span><span className="flex items-center gap-2">{eur(x.amount)}<button onClick={async () => { await mdel(`/expenses/${x.id}`); load(); }} className="text-rose-700" data-testid={`fixed-delete-${x.id}`}><Trash2 size={13} /></button></span></p>)}
      </Card>
    </div>
  );
}
